import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';
import { CheckCircle2, Calendar, Clock, MapPin, Phone, ArrowRight, Receipt } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiceBookingSuccessPage = () => {
  const [params] = useSearchParams();
  const bid = params.get('bid');
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/services/bookings/me`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        const b = (data.bookings || []).find((x) => x.booking_id === bid);
        if (b) setBooking(b);
      } catch (e) { /* noop */ }
    };
    if (bid) load();
  }, [bid]);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <Seo title="Booking Confirmed" description="Your service booking is confirmed." path="/services/booking-success" />
      <div className="container-luxury max-w-3xl">
        <div className="bg-white rounded-sm border border-border/50 p-8 md:p-12 text-center" data-testid="booking-success">
          <div className="h-20 w-20 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-[#10B981]" />
          </div>
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-3">Booking Confirmed!</h1>
          <p className="text-muted-foreground mb-2">Your booking ID:</p>
          <p className="font-mono text-lg font-bold text-[#D4AF37] mb-8" data-testid="booking-id">{bid}</p>

          {booking && (
            <div className="bg-[#F9F9F7] rounded-sm p-6 text-left space-y-4 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs uppercase text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" /> Date</p>
                  <p className="font-semibold text-[#0F172A] mt-0.5">{new Date(booking.slot_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> Time Slot</p>
                  <p className="font-semibold text-[#0F172A] mt-0.5">{booking.slot_time}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs uppercase text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Address</p>
                  <p className="font-semibold text-[#0F172A] mt-0.5">{booking.address_line1}, {booking.city} - {booking.pincode}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" /> Contact</p>
                  <p className="font-semibold text-[#0F172A] mt-0.5">{booking.contact_phone}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground flex items-center gap-1"><Receipt className="h-3 w-3" /> Payment</p>
                  <p className="font-semibold text-[#0F172A] text-base mt-0.5">Cash on Service</p>
                  <p className="text-xs text-muted-foreground">Quote shared on call · pay after the job</p>
                </div>
              </div>
              <div className="border-t border-border/50 pt-4">
                <p className="text-xs uppercase text-muted-foreground mb-2">Services Booked</p>
                <ul className="space-y-1 text-sm">
                  {booking.items.map((i) => (
                    <li key={i.service_id} className="flex justify-between">
                      <span>{i.name} × {i.quantity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <p className="text-muted-foreground text-sm mb-6">A confirmation SMS &amp; WhatsApp will arrive within 5 minutes. Our pro will reach 5 mins before the slot.</p>

          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/dashboard?tab=bookings">
              <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-6 py-5" data-testid="view-bookings-btn">
                View My Bookings <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="px-6 py-5">Book another</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceBookingSuccessPage;
